import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  AdminSupportingText,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  BILLING_CYCLE,
  BILLING_CYCLE_OPTIONS,
  GST_OPTIONS,
  PUBLIC_DESCRIPTION_MAX_LENGTH,
  buildSamplePricingPlanManagementRecords,
  emptyPricingPlanForm,
  formatAuditDisplay,
  formatEffectiveFromDisplay,
  formatGstDisplay,
  formatMonthlyCostFull,
  getAvailablePlanNames,
  hasPendingPriceChange,
  resolveCurrentMonthlyCost,
} from '../../../data/pricingPlanManagementData';
import {
  SAVE_FAILURE_MESSAGE,
  SAVE_SUCCESS_MESSAGE,
  collectFieldErrors,
  getPublishedPlan,
  mergePlansAfterSave,
  toFormPlan,
} from '../../../data/pricingPlanManagementWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function StackedField({ label, required, error, children }) {
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label}
        {required ? <span className="text-alertred"> *</span> : null}
      </AdminDetailFieldLabel>
      {children}
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
}

/** Trade Categories-style locked field: readonly input shell. */
function ReadOnlyField({ label, value, mono = false }) {
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      <input
        type="text"
        value={value == null || value === '' ? '—' : String(value)}
        readOnly
        className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
      />
    </div>
  );
}

function CurrencyAmountInput({ value, onChange, placeholder, hasError }) {
  return (
    <div className="relative w-full min-w-0">
      <input
        type="text"
        inputMode="decimal"
        className={`${adminFormDetailInputClasses(false, hasError)} pr-14 tabular-nums`}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
      />
      <span className="pointer-events-none absolute right-4 top-1/2 z-[1] -translate-y-1/2 text-xs text-mediumgray">
        AUD
      </span>
    </div>
  );
}

const PricingPlanDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planName: planNameParam } = useParams();
  const isAdd = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isAdd && !isEdit;

  const allPlans = useMemo(
    () => location.state?.pricingPlans ?? buildSamplePricingPlanManagementRecords(),
    [location.state?.pricingPlans],
  );

  const resolvedSource = useMemo(() => {
    if (isAdd) return emptyPricingPlanForm();
    const fromState = location.state?.plan;
    if (fromState && matchesUrlName(fromState.planName, planNameParam)) {
      return toFormPlan(fromState);
    }
    const found = allPlans.find((plan) => matchesUrlName(plan.planName, planNameParam));
    if (found) return toFormPlan(found);
    return null;
  }, [planNameParam, location.state?.plan, allPlans, isAdd]);

  const publishedBaseline = useMemo(
    () => (isAdd ? null : getPublishedPlan(resolvedSource)),
    [isAdd, resolvedSource],
  );

  const [formData, setFormData] = useState(resolvedSource || emptyPricingPlanForm());
  const [errors, setErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState('');

  const availablePlanNames = useMemo(
    () => getAvailablePlanNames(allPlans, isAdd ? undefined : formData.id),
    [allPlans, isAdd, formData.id],
  );

  const pendingExists = hasPendingPriceChange(formData);

  useEffect(() => {
    if (resolvedSource) setFormData({ ...resolvedSource });
  }, [resolvedSource]);

  const setField = (field, value) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setSaveMessage('');
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const navigateToList = (nextPlans, successMessage) => {
    navigate('/admin/pricing-plans', {
      state: {
        pricingPlans: nextPlans,
        ...(successMessage ? { saveSuccess: successMessage } : {}),
      },
    });
  };

  const handleSave = () => {
    const fieldErrors = collectFieldErrors(formData, allPlans, {
      excludeId: isAdd ? undefined : formData.id,
      publishedBaseline: isAdd ? null : publishedBaseline,
      isNew: isAdd,
    });
    if (Object.keys(fieldErrors).length) {
      setErrors(fieldErrors);
      setSaveMessage(SAVE_FAILURE_MESSAGE);
      return;
    }
    setErrors({});
    setSaveMessage('');
    const merged = mergePlansAfterSave(allPlans, formData, publishedBaseline, { isNew: isAdd });
    navigateToList(merged, SAVE_SUCCESS_MESSAGE);
  };

  if (!isAdd && !formData?.id && !resolvedSource?.id) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Pricing Plan not found</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">
          Pricing Plan not found.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  const pageHeading = isAdd
    ? 'Add Pricing Plan'
    : isEdit
      ? 'Edit Pricing Plan'
      : 'Pricing Plan Detail';

  const currentCostDisplay = formatMonthlyCostFull(resolveCurrentMonthlyCost(formData));

  const formFields = (
    <>
      {isAdd ? (
        <>
          <StackedField label="Plan Name" required error={errors.planName}>
            <div className="relative w-full min-w-0">
              <select
                className={adminFormDetailSelectClasses(false, Boolean(errors.planName))}
                value={formData.planName || ''}
                onChange={(e) => setField('planName', e.target.value)}
              >
                <option value="">Select Plan Name</option>
                {availablePlanNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
          </StackedField>
          <StackedField
            label="Initial Monthly Cost"
            required
            error={errors.initialMonthlyCost}
          >
            <CurrencyAmountInput
              value={formData.initialMonthlyCost}
              onChange={(e) => setField('initialMonthlyCost', e.target.value)}
              placeholder="0.00"
              hasError={Boolean(errors.initialMonthlyCost)}
            />
          </StackedField>
        </>
      ) : (
        <>
          <ReadOnlyField label="Plan ID" value={formData.id} mono />
          <ReadOnlyField label="Plan Name" value={formData.planName} />
          {isEdit ? (
            <StackedField
              label="Current Monthly Cost"
              required
              error={errors.currentMonthlyCost}
            >
              <CurrencyAmountInput
                value={formData.currentMonthlyCost}
                onChange={(e) => setField('currentMonthlyCost', e.target.value)}
                placeholder="0.00"
                hasError={Boolean(errors.currentMonthlyCost)}
              />
            </StackedField>
          ) : (
            <ReadOnlyField label="Current Monthly Cost" value={currentCostDisplay} />
          )}
        </>
      )}

      {isView || isEdit ? (
        <>
          {isEdit ? (
            <>
              <StackedField label="GST">
                <div className="relative w-full min-w-0">
                  <select
                    className={adminFormDetailSelectClasses(false)}
                    value={formData.gstIncluded === false ? 'Not Included' : 'Included'}
                    onChange={(e) => setField('gstIncluded', e.target.value === 'Included')}
                  >
                    {GST_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <AdminFormDropdownChevron />
                </div>
              </StackedField>
              <StackedField label="Billing Cycle">
                <div className="relative w-full min-w-0">
                  <select
                    className={adminFormDetailSelectClasses(false)}
                    value={formData.billingCycle || BILLING_CYCLE}
                    onChange={(e) => setField('billingCycle', e.target.value)}
                  >
                    {BILLING_CYCLE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <AdminFormDropdownChevron />
                </div>
              </StackedField>
            </>
          ) : (
            <>
              <ReadOnlyField label="GST" value={formatGstDisplay(formData.gstIncluded)} />
              <ReadOnlyField label="Billing Cycle" value={formData.billingCycle || BILLING_CYCLE} />
            </>
          )}
        </>
      ) : (
        <>
          <StackedField label="GST">
            <div className="relative w-full min-w-0">
              <select
                className={adminFormDetailSelectClasses(false)}
                value={formData.gstIncluded === false ? 'Not Included' : 'Included'}
                onChange={(e) => setField('gstIncluded', e.target.value === 'Included')}
              >
                {GST_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
          </StackedField>
          <StackedField label="Billing Cycle">
            <div className="relative w-full min-w-0">
              <select
                className={adminFormDetailSelectClasses(false)}
                value={formData.billingCycle || BILLING_CYCLE}
                onChange={(e) => setField('billingCycle', e.target.value)}
              >
                {BILLING_CYCLE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
          </StackedField>
        </>
      )}

      <StackedField
        label="Public Description"
        required={!isView}
        error={errors.publicDescription}
      >
        {isView ? (
          <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">
            {formData.publicDescription || '—'}
          </AdminDetailFieldValue>
        ) : (
          <textarea
            rows={4}
            maxLength={PUBLIC_DESCRIPTION_MAX_LENGTH}
            className={adminFormDetailTextareaClasses(false, Boolean(errors.publicDescription))}
            value={formData.publicDescription || ''}
            onChange={(e) => setField('publicDescription', e.target.value)}
            placeholder="Enter Public Description"
          />
        )}
      </StackedField>

      {isView && pendingExists ? (
        <div className="space-y-4">
          <AdminPanelTitle className="text-offblack !mb-0">Pending Price Change</AdminPanelTitle>
          <ReadOnlyField
            label="Pending Monthly Cost"
            value={formatMonthlyCostFull(formData.pendingMonthlyCost)}
          />
          <ReadOnlyField
            label="Effective From"
            value={formatEffectiveFromDisplay(formData.effectiveFrom)}
          />
        </div>
      ) : null}

      {isView || isEdit ? (
        <>
          <ReadOnlyField label="Added On" value={formatAuditDisplay(formData.addedOn)} />
          <ReadOnlyField label="Added By" value={formData.addedBy} />
          <ReadOnlyField
            label="Last Updated On"
            value={formatAuditDisplay(formData.lastUpdatedOn)}
          />
          <ReadOnlyField label="Last Updated By" value={formData.lastUpdatedBy} />
        </>
      ) : null}
    </>
  );

  if (isView) {
    return (
      <AdminLayout>
        <div className="max-w-[25rem] mx-auto w-full min-w-0">
          <div className="mb-6">
            <AdminPageTitle className="!mb-0">{pageHeading}</AdminPageTitle>
          </div>
          <div className="p-4 space-y-6 min-w-0">{formFields}</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={pageHeading}
        onSubmit={handleSave}
        showSave
        saveLabel="Save"
        beforeForm={
          saveMessage ? (
            <div className="mb-6">
              <AdminFormFieldError className="mb-0">{saveMessage}</AdminFormFieldError>
            </div>
          ) : null
        }
      >
        {formFields}
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default PricingPlanDetail;
